import { Component, OnInit, OnDestroy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
    TCI_COMPONENT_MODE,
    TCITenantSelectorType,
    ITCIApplicationPublishRequest,
    TCI_APP_DEPLOYMENT_STAGE,
    ITCIApplicationPublishResponse,
    ITCIApplicationDeployRequest,
    TCIApplicationDeploymentMode
} from "@tci/common";
import { TciTenantModelService } from "../../ng-tenant/tci-tenant.model";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../ng-app-details/ng-app-details-menu";
import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { AbstractTCITenantComponent } from "../../ng-tenant/tci-tenant-component";
import { ITCIAppDetailsMenuOutput } from "../../ng-tenant/tci-tenant-components";
import { BaseAppHMMarkLiveService } from "./base-app-hm-mark-live.service";
import { Observable, Subscription } from "rxjs";

interface IModalContent {
    TITLE: string;
    TITLE_ICON_CLASS?: string;
    DISCLAIMER: string;
    CONFIRM_APP_NAME_MSG?: string;
    CANCEL_BUTTON?: string;
    SUBMIT_BUTTON?: string;
}

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_MARK_LIVE.toString(),
    templateUrl: "./base-app-hm-mark-live.component.html",
    styleUrls: ["./base-app-hm-mark-live.component.less"],
})
export class BaseAppHMMarkLiveComponent<E, I extends BaseAppDetailMenuComponentInput,
    O extends BaseAppDetailMenuComponentOutput<ITCIAppDetailsMenuOutput>>
    extends AbstractTCITenantComponent<E, I, O>
    implements OnInit, OnDestroy {

    isHM = false; // To toggle mode
    alive = false;
    routerLink: string[];

    currentAppName: string;

    private modalContent: IModalContent;
    private needConfirmApp = false;

    subscriptions: Subscription[] = [];

    constructor(
        private tenantModelSvc: TciTenantModelService,
        private markLiveService: BaseAppHMMarkLiveService,
        private translate: TranslateService,
        private toastr: NgToastrService
    ) {
        super();

        this.subscriptions.push(
            this.markLiveService.getStatusSubject()
                .filter(status => status.appId === this.input.application.id)
                .map(status => status.stage)
                .subscribe(status => this.alive = status === TCI_APP_DEPLOYMENT_STAGE.PUBLISHED)
        );
    }

    ngOnInit(): void {
        this.alive = this.input.application.publishStage === TCI_APP_DEPLOYMENT_STAGE.PUBLISHED;
        this.isHM = this.input.mode === TCI_COMPONENT_MODE.COMP_MODE_APP_DETAIL_HAMBURGER_MENU;
        if (this.isHM) {
            if (this.alive) {
                this.needConfirmApp = true;
                this.currentAppName = this.input.application.name;
            }
            this.modalContent = this.getNotBusyModalContent();
            const stateRequest: ITCIApplicationDeployRequest = {
                mode: TCIApplicationDeploymentMode.MODE_APP_HAM_MENU_MARK_LIVE,
                appId: this.input.application.id,
                application: this.input.application,
                tenantType: this.input.application.type
            };
            /*
            Note: hide this pre-validate dialog, use the this.onSubmit(), error callback toastr.error()
            const provider = this.tenantModelSvc.getTenant(this.input.application.type).getAppDeploymentProvider();
            Observable.combineLatest(
                provider.isBusy(stateRequest),
                (isBusy) => (
                    { isBusy }
                )).subscribe(res => {
                    if (res.isBusy) {
                        this.modalContent = this.getBusyModalContent();
                    }
                });
             */
        } else if (this.input.componentOption) {
            this.routerLink = ["../../../details", this.input.componentOption.getRouteId(), this.input.application.id];
        }
    }

    ngOnDestroy(): void {
        this.subscriptions
            .filter(s => !s.closed)
            .forEach(s => s.unsubscribe());
    }

    getBusyModalContent() {
        return {
            TITLE: this.alive ? this.translate.instant("TCI_HM_MARK_LIVE.TITLE_UNPUBLISH") : this.translate.instant("TCI_HM_MARK_LIVE.TITLE_PUBLISH"),
            DISCLAIMER: this.translate.instant("TCI_HM_MARK_LIVE.DISCLAIMER_BUSY"),
            CANCEL_BUTTON: "Cancel",
        };
    }

    getNotBusyModalContent() {
        return {
            TITLE: this.alive ? this.translate.instant("TCI_HM_MARK_LIVE.TITLE_UNPUBLISH") : this.translate.instant("TCI_HM_MARK_LIVE.TITLE_PUBLISH"),
            TITLE_ICON_CLASS: this.alive ? "published-app-icon" : "",
            DISCLAIMER: this.alive ? this.translate.instant("TCI_HM_MARK_LIVE.DISCLAIMER_UNPUBLISH") : this.translate.instant("TCI_HM_MARK_LIVE.DISCLAIMER_PUBLISH"),
            CONFIRM_APP_NAME_MSG: (
                this.alive ? this.translate.instant("TCI_HM_MARK_LIVE.CONFIRM_APP_NAME_UNPUBLISHED") : this.translate.instant("TCI_HM_MARK_LIVE.CONFIRM_APP_NAME_PUBLISHED")
            ),
            CANCEL_BUTTON: "Cancel",
            SUBMIT_BUTTON: this.alive ? this.translate.instant("TCI_HM_MARK_LIVE.CONFIRM_BUTTON_UNPUBLISH") : this.translate.instant("TCI_HM_MARK_LIVE.CONFIRM_BUTTON_PUBLISH")
        };
    }

    onSubmit(): void {
        const inputObj = this.input as BaseAppDetailMenuComponentInput;
        const stage = this.alive ? TCI_APP_DEPLOYMENT_STAGE.DRAFT : TCI_APP_DEPLOYMENT_STAGE.PUBLISHED;

        const appReq: ITCIApplicationPublishRequest = {
            appId: inputObj.application.id,
            application: this.input.application,
            msg: null,
            tenantType: inputObj.tenantAppType,
            stage: stage as TCI_APP_DEPLOYMENT_STAGE
        };

        this.markLiveService.updateMarkAlive(appReq).subscribe(
            (rsp: ITCIApplicationPublishResponse) => {
                this.input.application.publishStage = stage as TCI_APP_DEPLOYMENT_STAGE;

                this.markLiveService.publishStatus(this.input.application.id, this.input.application.publishStage);
                this.toastr.clear();
                this.toastr.success(this.input.application.name, rsp.msg);
                this.closeModal(true);
            },
            (err) => {
                this.toastr.clear();
                this.toastr.error(this.input.application.name, err.json().msg);
                this.closeModal();
            }
        );
    }

    onCancel(): void {
        this.closeModal();
    }

    protected closeModal(refresh = false): ITCIAppDetailsMenuOutput {
        const e: ITCIAppDetailsMenuOutput = {
            menuItemSelector: this.input.componentSelector,
            closeModal: true,
            refresh
        };
        this.output.emit(e);
        return e;
    }
}
